import qs from 'qs';
import { createResource } from 'react-resource-router';

import { PIPELINE_USAGE_TABLE_PAGE_SIZE } from 'src/components/pipelines/constants';
import { getAllowanceUrl } from 'src/components/pipelines/resources/allowance';
import envBaseUrl from 'src/components/pipelines/utils/env-base-url';
import {
  capturePipelinesExceptionWithTags,
  createErrorMessage,
} from 'src/components/pipelines/utils/sentry';
import { ResourceContext } from 'src/router/types';
import { authRequest, jsonHeaders } from 'src/utils/fetch';

import { parseDateRange } from '../pages/admin/pipelines-usage-details/utils';

import { fetchData } from './utils';

export type Params = {
  page: string;
};

interface RepositoryUsage {
  repository: {
    name: string;
    uuid: string;
  };
  totalUsageInMinutes: number;
  usageChangeInPercentage: number;
}

export const getPipelineUsageDetailsUrl = (
  accountUuid: string,
  newQuery: string
) => {
  return `${envBaseUrl(
    '/!api/internal'
  )}/accounts/${accountUuid}/pipelines/build-minute-usage?${newQuery}`;
};

function transformUuids(uuids: string): string[] {
  return uuids.split(',').map(uuid => `{${uuid.trim().replace(/[{}]/g, '')}}`);
}

export const loadPipelineUsageDetailsResource = createResource({
  type: 'pipeline-usage-details',
  getKey: ({ query }) =>
    `pipeline-usage-details:${query.page}:${query.dateRange}:${query.sort}:${query.repositoryUuids}`,
  maxAge: 0,
  getData: async (params, { reduxStore }: ResourceContext) => {
    const { targetUser } = reduxStore.getState().global;
    if (!targetUser) {
      throw new Error('Target user not found');
    }

    let allowanceData;

    try {
      allowanceData = await fetchData(
        getAllowanceUrl(targetUser),
        'allowance_resource_exception'
      );
    } catch (e) {
      capturePipelinesExceptionWithTags(e, {
        segment: 'PIPELINES_FAILED_TO_FETCH_ALLOWANCE',
      });
      return {
        page: 1,
        repoUsages: [],
        total: 0,
      };
    }
    let { startDate, endDate } = parseDateRange(params.query.dateRange || '');
    if (!startDate || !endDate) {
      startDate = allowanceData?.start_date;
      endDate = allowanceData?.end_date;
    }

    if (!startDate || !endDate) {
      throw new Error('Invalid allowance dates');
    }

    const repositoryUuidsArray = params.query.repositoryUuids
      ? transformUuids(params.query.repositoryUuids)
      : [];
    const pageToUse = repositoryUuidsArray.length
      ? '1'
      : params.query.page || '1';

    try {
      const newQuery = qs.stringify({
        page: pageToUse,
        pagelen: PIPELINE_USAGE_TABLE_PAGE_SIZE,
        startDate,
        endDate,
        sort: params.query.sort,
      });

      const requestBody = {
        repositoryUuids: repositoryUuidsArray,
      };

      const response = await fetch(
        authRequest(getPipelineUsageDetailsUrl(targetUser, newQuery), {
          method: 'POST',
          headers: jsonHeaders,
          body: JSON.stringify(requestBody),
        })
      );
      if (!response.ok) {
        throw new Error(await createErrorMessage(response));
      }

      const data = await response.json();
      const { page, size: total, values } = data;

      const reposWithUsageUuids = values.map(
        (usage: RepositoryUsage) => usage.repository.uuid
      );
      const reposWithoutUsage = repositoryUuidsArray.filter(
        uuid => !reposWithUsageUuids.includes(uuid)
      );

      const repoUsages = values.map((usage: RepositoryUsage) => ({
        repositoryName: usage.repository?.name,
        repositoryUuid: usage.repository.uuid,
        totalUsageInMinutes: usage.totalUsageInMinutes,
        usageChangeInPercentage: usage.usageChangeInPercentage,
      }));

      return { page, total, repoUsages, reposWithoutUsage };
    } catch (e) {
      capturePipelinesExceptionWithTags(e, {
        segment: 'PIPELINES_FAILED_TO_FETCH_PIPELINE_USAGE_DETAILS',
      });
      return {
        page: 1,
        repoUsages: [],
        total: 0,
        reposWithoutUsage: [],
      };
    }
  },
});
